import { useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import type { BillingClientSummary } from "@school-sales-ai/shared";
import {
  useBillingProducts,
  useCreatePaymentLink,
  useDeletePaymentLink,
} from "@/hooks/useBilling";
import { formatMxn } from "@/lib/format-mxn";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type PaymentLinkModalProps = {
  client: BillingClientSummary;
  onClose: () => void;
};

// Stripe rejects anything under $10.00 MXN outright, so this is a hard floor and
// not a pricing opinion.
const MXN_MINIMUM = 10;

// The Escuela band from PRICING_AND_UNIT_ECONOMICS.md §2.5. Shown as guidance and
// never enforced: the super-admin IS the house approval that section requires.
const BAND_FLOOR = 6_990;
const BAND_LIST = 9_990;

function pesosToCents(value: string): number {
  return Math.round(Number(value.replace(/[^0-9.]/g, "")) * 100);
}

export function PaymentLinkModal({
  client,
  onClose,
}: PaymentLinkModalProps): React.JSX.Element {
  const { data: products, isPending: loadingProducts } = useBillingProducts(client.clientId);
  const noProducts = !loadingProducts && (products ?? []).length === 0;
  const createLink = useCreatePaymentLink();
  const deleteLink = useDeletePaymentLink();

  const [productId, setProductId] = useState("");
  const [monthly, setMonthly] = useState("");
  const [setup, setSetup] = useState("");
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [taxBehavior, setTaxBehavior] = useState<"exclusive" | "inclusive">("exclusive");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const existing = client.paymentLinkUrl;
  const monthlyCents = monthly ? pesosToCents(monthly) : 0;
  const belowBand = monthlyCents > 0 && monthlyCents < BAND_FLOOR * 100;

  // What the school is actually charged, and what is left after IVA. This is the
  // point of the selector: the 16% error is silent and only shows up at the charge,
  // so the form states both numbers rather than leaving them to be inferred.
  const IVA = 0.16;
  const charged =
    taxBehavior === "exclusive"
      ? Math.round(monthlyCents * (1 + IVA))
      : monthlyCents;
  const net =
    taxBehavior === "exclusive" ? monthlyCents : Math.round(monthlyCents / (1 + IVA));

  function submit(): void {
    if (!productId) {
      setError("Elige el producto que verá la escuela en su factura.");
      return;
    }
    if (monthlyCents < MXN_MINIMUM * 100) {
      setError(`La mensualidad mínima que Stripe acepta es ${formatMxn(MXN_MINIMUM * 100)}.`);
      return;
    }
    const setupCents = setup ? pesosToCents(setup) : 0;
    if (setupCents > 0 && setupCents < MXN_MINIMUM * 100) {
      setError(`La implementación debe ser 0 o al menos ${formatMxn(MXN_MINIMUM * 100)}.`);
      return;
    }
    setError(null);
    createLink.mutate(
      { clientId: client.clientId, productId, monthlyCents, setupCents, interval, taxBehavior },
      { onSuccess: onClose },
    );
  }

  async function copy(): Promise<void> {
    if (!existing) return;
    await navigator.clipboard.writeText(existing);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link de cobro</DialogTitle>
          <DialogDescription>
            {client.clientName} —{" "}
            {existing
              ? "Este es el link vigente. Regenerarlo desactiva el anterior."
              : "Genera el link que la escuela abre para pagar. Queda asociado a esta escuela, así que el cobro se identifica solo."}
          </DialogDescription>
        </DialogHeader>

        {existing ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 rounded-lg border border-ink-100 bg-ink-050 p-3">
              <span className="flex-1 truncate font-mono text-xs text-ink-700">{existing}</span>
              <Button variant="outline" size="sm" onClick={() => void copy()}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </Button>
              <a href={existing} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm">
                  <ExternalLink size={14} />
                </Button>
              </a>
            </div>
            <p className="text-xs text-ink-500">
              Se puede completar una sola vez. Si la escuela lo reabre después de pagar,
              Stripe lo rechaza en vez de cobrarle una segunda suscripción.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              disabled={deleteLink.isPending}
              onClick={() => deleteLink.mutate(client.clientId, { onSuccess: onClose })}
            >
              Desactivar este link
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pl-product" className="text-xs font-medium text-ink-700">
                Producto (lo que se lee en la factura)
              </label>
              <select
                id="pl-product"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="h-9 rounded-md border border-ink-200 bg-surface px-3 text-sm text-ink-900"
              >
                <option value="">
                  {loadingProducts ? "Cargando…" : "Elige un producto"}
                </option>
                {(products ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {noProducts && (
                <p className="text-xs text-ink-500">
                  No hay planes activos en Stripe para la marca de este cliente. Créalos con el
                  nombre «Marca — Plan» (por ejemplo, «Loxago — Tienda»).
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="pl-monthly" className="text-xs font-medium text-ink-700">
                  Mensualidad (MXN)
                </label>
                <Input
                  id="pl-monthly"
                  value={monthly}
                  onChange={(e) => setMonthly(e.target.value)}
                  placeholder="9990"
                  inputMode="decimal"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="pl-setup" className="text-xs font-medium text-ink-700">
                  Implementación (MXN)
                </label>
                <Input
                  id="pl-setup"
                  value={setup}
                  onChange={(e) => setSetup(e.target.value)}
                  placeholder="0 si no se cobra"
                  inputMode="decimal"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-700">Periodicidad</label>
              <div className="flex gap-2">
                {(["month", "year"] as const).map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setInterval(i)}
                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                      interval === i
                        ? "border-ink-900 bg-ink-900 text-white"
                        : "border-ink-200 text-ink-700 hover:bg-ink-050"
                    }`}
                  >
                    {i === "month" ? "Mensual" : "Anual"}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-700">IVA</label>
              <div className="flex gap-2">
                {(
                  [
                    ["exclusive", "+ IVA"],
                    ["inclusive", "IVA incluido"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTaxBehavior(value)}
                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                      taxBehavior === value
                        ? "border-ink-900 bg-ink-900 text-white"
                        : "border-ink-200 text-ink-700 hover:bg-ink-050"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {monthlyCents > 0 && (
              <div className="rounded-md bg-ink-050 px-3 py-2 text-xs text-ink-700">
                <div className="flex justify-between">
                  <span>La escuela paga cada mes</span>
                  <strong className="text-ink-900">{formatMxn(charged)}</strong>
                </div>
                <div className="mt-0.5 flex justify-between text-ink-500">
                  <span>Tu ingreso antes de IVA</span>
                  <span>{formatMxn(net)}</span>
                </div>
              </div>
            )}

            {belowBand && (
              <p className="rounded-md bg-[oklch(0.97_0.03_75)] px-3 py-2 text-xs text-ink-700">
                Debajo del piso de {formatMxn(BAND_FLOOR * 100)}. La banda de Escuela va de{" "}
                {formatMxn(BAND_FLOOR * 100)} a {formatMxn(BAND_LIST * 100)}; por debajo
                requiere aprobación de la casa.
              </p>
            )}

            {error && <p className="text-xs text-[oklch(0.55_0.18_16)]">{error}</p>}

            <p className="text-xs text-ink-500">
              Deja la implementación vacía o en 0 si no se cobra. La modalidad de IVA
              queda fija en el precio: cambiarla después requiere emitir un precio
              nuevo.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            {existing ? "Cerrar" : "Cancelar"}
          </Button>
          {!existing && (
            <Button size="sm" onClick={submit} disabled={createLink.isPending}>
              {createLink.isPending ? "Generando…" : "Generar link"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
